import { remove } from "lodash"
import { isDefined } from "@webapp/util/TypeScriptUtil"
import { Person } from "@webapp/rd/ResearchDataModel"
import { WorkspaceSourceGraph } from "./SyncSessionModel"

export enum RelationshipCategory {
    Ancestor,
    Parent,
    Self,
    Sibling,
    Child,
    Descendant,
    ParentSibling,
    Cousin,
    SiblingChild,
    Other,
}

export const orderedRelationshipCategories = [
    RelationshipCategory.Ancestor,
    RelationshipCategory.Parent,
    RelationshipCategory.Self,
    RelationshipCategory.Sibling,
    RelationshipCategory.Child,
    RelationshipCategory.Descendant,
    RelationshipCategory.ParentSibling,
    RelationshipCategory.Cousin,
    RelationshipCategory.SiblingChild,
    RelationshipCategory.Other,
] as ReadonlyArray<RelationshipCategory>

export function getRelationshipCategoryText(category: RelationshipCategory) {
    switch (category) {
        case RelationshipCategory.Ancestor: return "Ancestors and spouses"
        case RelationshipCategory.Parent: return "Parents and spouses"
        case RelationshipCategory.Self: return "Self and spouses"
        case RelationshipCategory.Sibling: return "Siblings and spouses"
        case RelationshipCategory.Child: return "Children and spouses"
        case RelationshipCategory.Descendant: return "Descendants and spouses"
        case RelationshipCategory.ParentSibling: return "Aunts and uncles and spouses"
        case RelationshipCategory.Cousin: return "First cousins and spouses"
        case RelationshipCategory.SiblingChild: return "Nephews and nieces and spouses"
        case RelationshipCategory.Other: return "Other people"
    }
}

export function buildLivingWindowGroups(
    graph: WorkspaceSourceGraph,
    startPersonId?: string,
): Map<RelationshipCategory, Person[]> {
    const groups = new Map<RelationshipCategory, Person[]>()

    const self = graph.persons.get(startPersonId ?? "")
    if (self?.id) {
        const parents = graph.getParents(self.id)
        const ancestors = parents.flatMap(p => [...graph.getAncestors(p.id!, 5)])
        const parentSiblings = parents.flatMap(p => graph.getParentFamilies(p.id!))
            .flatMap(pf => graph.getChildren(pf.id!))
            .filter(s => !parents.includes(s))
        const cousins = parentSiblings.flatMap(ps => graph.getSpouseFamilies(ps.id!))
            .flatMap(sf => graph.getChildren(sf.id!))
        const siblings = graph.getParentFamilies(self.id)
            .flatMap(pf => graph.getChildren(pf.id!))
            .filter(s => s != self)
        const siblingChildren = siblings.flatMap(s => graph.getSpouseFamilies(s.id!))
            .flatMap(sf => graph.getChildren(sf.id!))
        const children = graph.getSpouseFamilies(self.id)
            .flatMap(sf => graph.getChildren(sf.id!))
        const grandChildren = children.flatMap(c => graph.getSpouseFamilies(c.id!))
            .flatMap(sf => graph.getChildren(sf.id!))
        const descendants = [...grandChildren,
            ...grandChildren.flatMap(c => [...graph.getDescendants(c.id!, 3)])]

        groups.set(RelationshipCategory.Ancestor, ancestors)
        groups.set(RelationshipCategory.Parent, parents)
        groups.set(RelationshipCategory.Self, [self])
        groups.set(RelationshipCategory.Sibling, siblings)
        groups.set(RelationshipCategory.Child, children)
        groups.set(RelationshipCategory.Descendant, descendants)
        groups.set(RelationshipCategory.ParentSibling, parentSiblings)
        groups.set(RelationshipCategory.Cousin, cousins)
        groups.set(RelationshipCategory.SiblingChild, siblingChildren)

        for (const g of groups.values()) {
            const spouses = g.flatMap(p => graph.getSpousesOf(p.id!))
            const personsAndSpouses = new Set([...g, ...spouses])
            g.length = 0
            g.push(...personsAndSpouses)
        }

        const allRelatedIds = new Set<string>([...groups.values()].flat().map(p => p.id!))
        const other = [...graph.livingWindowPersonIds]
            .filter(id => !allRelatedIds.has(id))
            .map(id => graph.persons.get(id))
            .filter(isDefined)
        groups.set(RelationshipCategory.Other, other)
    }
    else {
        groups.set(RelationshipCategory.Other,
            [...graph.livingWindowPersonIds]
                .map(id => graph.persons.get(id))
                .filter(isDefined))
    }

    for (const g of groups.values()) {
        remove(g, p => !graph.livingWindowPersonIds.has(p.id!))
        g.sort(Person.compareDisplayName)
    }

    return groups
}
